import { useState, useRef, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { X, RotateCcw, Send, Clock } from "lucide-react";
import "../assets/styles/chat-widget.css";
import {
  sendLabAssistantMessage,
  getLabAssistantHistory,
} from "../api/services/labAssistantService";

const QUICK_REPLIES = [
  "Can you help me understand this exploit?",
  "What kind of box is this?",
  "I'm stuck on a question",
];

const INITIAL_MESSAGES = [
  {
    id: 1,
    from: "bot",
    text: "Hi! 👋 Need help with this box?\n\nHow can I assist you?",
  },
];

// ─── Helpers ───
function mapServerMessages(serverMessages) {
  return serverMessages.map((m) => ({
    id: m.id,
    from: m.role === "assistant" ? "bot" : "user",
    text: m.content,
  }));
}

function formatTokens(n) {
  if (n == null) return "";
  if (n < 1000) return String(n);
  const k = n / 1000;
  return `${k.toFixed(1).replace(/\.0$/, "")}k`;
}

// mm:ss for the countdown
function formatCountdown(ms) {
  const totalSec = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function parseResetAt(payload) {
  if (!payload) return null;

  const raw = payload.resetsAt ?? payload.resets_at;
  if (raw == null) return null;

  const t = typeof raw === "number" ? raw : new Date(raw).getTime();
  return Number.isNaN(t) ? null : t;
}

// ─── Sub-components ───
function BotAvatar() {
  return <div className="cw-avatar">QV</div>;
}

function TokenBar({ tokensUsed, tokensRemaining }) {
  if (tokensRemaining == null || tokensUsed == null) return null;

  const total = tokensUsed + tokensRemaining;
  const usedPct = total > 0 ? (tokensUsed / total) * 100 : 0;

  // fills up as you spend; turns amber/red as you approach the cap
  const fillClass =
    usedPct < 75
      ? "cw-token-fill--ok"
      : usedPct < 90
        ? "cw-token-fill--warn"
        : "cw-token-fill--danger";

  return (
    <div className="cw-token-bar-wrap shrink-0">
      <div className="cw-token-track">
        <div
          className={`cw-token-fill ${fillClass}`}
          style={{ width: `${usedPct}%` }}
        />
      </div>
      <span className="cw-token-label">
        {formatTokens(tokensUsed)} / {formatTokens(total)}
      </span>
    </div>
  );
}

// ─── Markdown renderers (shared) ───
function makeMarkdownComponents(from) {
  const isUser = from === "user";
  return {
    p: ({ children }) => <p className="mb-1 last:mb-0">{children}</p>,
    strong: ({ children }) => (
      <strong className={isUser ? "cw-strong-user" : "cw-strong-bot"}>
        {children}
      </strong>
    ),
    em: ({ children }) => <em className="italic">{children}</em>,
    code: ({ className, children, ...props }) => {
      const isBlock = Boolean(className);
      if (isBlock) {
        return (
          <code className="block" {...props}>
            {children}
          </code>
        );
      }
      return (
        <code
          className={isUser ? "cw-code-inline-user" : "cw-code-inline-bot"}
          {...props}
        >
          {children}
        </code>
      );
    },
    pre: ({ children }) => (
      <pre className={isUser ? "cw-pre-user" : "cw-pre-bot"}>{children}</pre>
    ),
    ul: ({ children }) => (
      <ul className="list-disc list-inside mb-1">{children}</ul>
    ),
    ol: ({ children }) => (
      <ol className="list-decimal list-inside mb-1">{children}</ol>
    ),
    a: ({ href, children }) => (
      <a
        href={href}
        className="underline"
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    ),
    blockquote: ({ children }) => (
      <blockquote className="cw-blockquote">{children}</blockquote>
    ),
  };
}

// ─── Locked input overlay (shown when the token budget is spent) ───
function LockedNotice({ msRemaining, hasCountdown }) {
  return (
    <div className="cw-locked">
      <Clock className="cw-locked-icon" />
      <div className="cw-locked-text">
        <span className="cw-locked-title">Message limit reached</span>
        <span className="cw-locked-sub">
          {hasCountdown ? (
            <>
              Resets in <strong>{formatCountdown(msRemaining)}</strong>
            </>
          ) : (
            "Please try again later"
          )}
        </span>
      </div>
    </div>
  );
}

// ─── Main Widget ───
export default function ChatWidget({ scenarioId }) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState(INITIAL_MESSAGES);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [quickRepliesVisible, setQuickRepliesVisible] = useState(true);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [tokensUsed, setTokensUsed] = useState(null);
  const [tokensRemaining, setTokensRemaining] = useState(null);

  // Lockout state
  const [resetAt, setResetAt] = useState(null); // epoch ms, or null when not timed-locked
  const [lockedNoTime, setLockedNoTime] = useState(false); // exhausted, no reset time
  const [now, setNow] = useState(Date.now()); // ticks every second while locked

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const hasCountdown = resetAt != null && now < resetAt;
  const isLocked = hasCountdown || lockedNoTime;
  const msRemaining = hasCountdown ? resetAt - now : 0;

  // Auto-focus input when panel opens (only if not locked)
  useEffect(() => {
    if (isOpen && !isLocked) setTimeout(() => inputRef.current?.focus(), 300);
  }, [isOpen, isLocked]);

  // Scroll to bottom on new messages / typing indicator
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);


  const applyTokenState = (used, remaining, payload) => {
    const parsed = parseResetAt(payload);
    const exhausted = remaining != null && remaining <= 0;
    const hasFutureReset = parsed != null && parsed > Date.now();
    // Window has elapsed if there's a reset time but it's already in the past.
    const windowElapsed = parsed != null && parsed <= Date.now();

    if (used != null) setTokensUsed(used);
    if (remaining != null) setTokensRemaining(remaining);

    if (hasFutureReset) {
      // Limit reached → lock with a live countdown.
      setResetAt(parsed);
      setLockedNoTime(false);
    } else if (exhausted && parsed == null) {
      // Exhausted with no reset time at all → lock without a countdown.
      setResetAt(null);
      setLockedNoTime(true);
    } else {
      // Budget available, or the reset window has already passed → unlock.
      setResetAt(null);
      setLockedNoTime(false);

      if (windowElapsed && exhausted) {
        const total = (used ?? 0) + (remaining ?? 0);
        setTokensUsed(0);
        setTokensRemaining(total > 0 ? total : null);
      }
    }
  };

  // Load persisted history for the current scenario
  const loadHistory = async () => {
    if (!scenarioId) return;

    setIsLoadingHistory(true);
    setIsTyping(false);
    setInput("");

    try {
      const data = await getLabAssistantHistory(scenarioId);

      const serverMessages = data.messages ?? [];
      const used = data.tokensUsed ?? data.tokens_used;
      const remaining = data.tokensRemaining ?? data.tokens_remaining;

      applyTokenState(used, remaining, data);

      if (serverMessages.length > 0) {
        setMessages(mapServerMessages(serverMessages));
        setQuickRepliesVisible(false);
      } else {
        setMessages(INITIAL_MESSAGES);
        setQuickRepliesVisible(true);
      }
    } catch {
      // On failure, fall back to a fresh greeting so the chat still works
      setMessages(INITIAL_MESSAGES);
      setQuickRepliesVisible(true);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // Countdown ticker — runs only while locked. When it reaches the reset
  useEffect(() => {
    if (resetAt == null) return;

    let fired = false;
    const tick = () => {
      const current = Date.now();
      setNow(current);
      if (current >= resetAt && !fired) {
        fired = true;
        setResetAt(null);
        setLockedNoTime(false);

        // Reset the bar to empty, preserving total capacity (used + remaining).
        const total = (tokensUsed ?? 0) + (tokensRemaining ?? 0);
        setTokensUsed(0);
        setTokensRemaining(total > 0 ? total : null);
      }
    };

    tick(); // sync immediately
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetAt]);

  // Reload history when scenario changes or the panel is opened
  useEffect(() => {
    if (isOpen) loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenarioId, isOpen]);

  // ── Handlers ──
  const sendMessage = async (text) => {
    const trimmed = text.trim();
    if (!trimmed || isTyping || isLocked) return;
    if (!scenarioId) {
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now(),
          from: "bot",
          text: "Box details are still loading. Please try again soon.",
        },
      ]);
      return;
    }

    // Optimistically add user message
    setMessages((prev) => [
      ...prev,
      { id: Date.now(), from: "user", text: trimmed },
    ]);
    setInput("");
    setQuickRepliesVisible(false);
    setIsTyping(true);

    try {
      const data = await sendLabAssistantMessage({
        scenarioId,
        message: trimmed,
      });
      const reply = data.reply;
      // Tolerate either snake_case or camelCase from the service layer.
      const used = data.tokens_used ?? data.tokensUsed;
      const remaining = data.tokens_remaining ?? data.tokensRemaining;

      applyTokenState(used, remaining, data);

      setMessages((prev) => [
        ...prev,
        { id: Date.now() + 1, from: "bot", text: reply },
      ]);
    } catch (err) {
      // If the backend rejects with a rate-limit error, lock the input.
      // Use applyTokenState so the resets_at countdown is handled uniformly.
      const payload = err?.response?.data;
      const status = err?.response?.status;
      if (status === 429 && payload) {
        const used = payload.tokens_used ?? payload.tokensUsed;
        applyTokenState(used, 0, payload);
      }

      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          from: "bot",
          text: `⚠️ ${err.message ?? "Something went wrong. Please try again."}`,
        },
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const canSend = Boolean(input.trim()) && !isTyping && !isLocked;
  const inputDisabled = isTyping || isLoadingHistory || isLocked;

  // ── Render ────
  return (
    <>
      {/* ── Chat Panel ── */}
      <div className={`cw-panel-wrap ${isOpen ? "is-open" : ""}`}>
        <div className="cw-panel">
          {/* Header */}
          <div className="cw-header">
            <h2 className="cw-header-title">Scenario Advisor</h2>
            <div className="flex items-center gap-1">
              <button className="cw-icon-btn cw-icon-btn--sm" disabled>
                <RotateCcw className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="cw-icon-btn cw-icon-btn--lg"
                aria-label="Close chat"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="cw-messages">
            {isLoadingHistory ? (
              <div className="cw-loading">Loading conversation…</div>
            ) : (
              <>
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`cw-row ${msg.from === "user" ? "cw-row--user" : "cw-row--bot"}`}
                  >
                    {msg.from === "bot" && <BotAvatar />}
                    <div
                      className={`cw-bubble ${
                        msg.from === "user"
                          ? "cw-user-bubble cw-bubble--user"
                          : "cw-bot-bubble cw-bubble--bot"
                      }`}
                    >
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={makeMarkdownComponents(msg.from)}
                      >
                        {msg.text}
                      </ReactMarkdown>
                    </div>
                  </div>
                ))}

                {/* Typing indicator */}
                {isTyping && (
                  <div className="cw-row cw-row--bot">
                    <BotAvatar />
                    <div className="cw-typing-bg cw-typing">
                      {[0, 1, 2].map((i) => (
                        <span
                          key={i}
                          className="cw-typing-dot"
                          style={{ animationDelay: `${i * 0.2}s` }}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Quick replies (hidden while locked) */}
                {quickRepliesVisible && !isTyping && !isLocked && (
                  <div className="cw-quick-wrap">
                    {QUICK_REPLIES.map((reply) => (
                      <button
                        key={reply}
                        type="button"
                        onClick={() => sendMessage(reply)}
                        className="cw-quick-btn"
                      >
                        {reply}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Token usage bar */}
          <TokenBar tokensUsed={tokensUsed} tokensRemaining={tokensRemaining} />

          {/* Input — replaced by a locked notice when over the limit */}
          {isLocked ? (
            <LockedNotice msRemaining={msRemaining} hasCountdown={hasCountdown} />
          ) : (
            <div className="cw-input-area">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Write here..."
                className="cw-input flex-1"
                disabled={inputDisabled}
              />
              <button
                type="button"
                onClick={() => sendMessage(input)}
                className={`cw-send-btn ${canSend ? "is-visible" : ""}`}
                aria-label="Send"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Floating Bubble (hidden when panel is open) ── */}
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className={`cw-bubble-btn ${isOpen ? "cw-bubble-hidden" : ""}`}
        aria-label="Open chat"
        aria-hidden={isOpen}
        tabIndex={isOpen ? -1 : 0}
      >
        <span className="cw-online-dot" />
        <svg
          width="24"
          height="24"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.8}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8 10h.01M12 10h.01M16 10h.01M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2v10z"
          />
        </svg>
      </button>
    </>
  );
}